import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { GoChevronDown } from "react-icons/go";
import { Language } from "./Languages";

interface LanguageSwitchHamburgerProps {
  setIsAllMenuOpen: Dispatch<SetStateAction<boolean>>;
  languages?: Language[];
}

function LanguageSwitchHamburger({ setIsAllMenuOpen, languages }: Readonly<LanguageSwitchHamburgerProps>) {
  const [t, i18n] = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [isCurrentMenuOpen, setIsCurrentMenuOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Gestisce quando il dropdown dovrebbe essere renderizzato
  useEffect(() => {
    if (isCurrentMenuOpen) {
      setShouldRender(true);
    } else {
      // Delay per permettere all'animazione di completarsi
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isCurrentMenuOpen]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    
    // Update URL with language parameter
    const searchParams = new URLSearchParams(location.search);
    if (lng === 'it') {
      // Remove lang parameter for default Italian
      searchParams.delete('lang');
    } else {
      searchParams.set('lang', lng);
    }
    
    const newSearch = searchParams.toString();
    const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
    navigate(newPath, { replace: true });
  };

  // Get selected language object
  const selectedLanguage = languages?.find((l) => l.Value === language);

  return (
    <div className="w-full relative">
      {/* Pulsante principale per le lingue */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsCurrentMenuOpen(!isCurrentMenuOpen);
        }}
        className="w-full  shadow-md transition duration-200 text-left rounded-md pl-[20px] py-4 text-lg sm:py-3 sm:text-base bg-transparent hover:bg-lightbg-headerSecondary hover:scale-105 flex items-center justify-between"
        type="button"
        aria-expanded={isCurrentMenuOpen ? "true" : "false"}
        aria-label={`${t('language')}: ${selectedLanguage?.Name}`}
      >
        <span className="flex-1">
          {selectedLanguage?.Name}
        </span>
        <GoChevronDown 
          className={`mr-4 transition-transform duration-200 ${
            isCurrentMenuOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Sottomenu delle lingue con animazione */}
      {shouldRender && (
        <div 
          className={`w-full relative z-[999999] mt-2 ${
            isCurrentMenuOpen ? 'animate-[slideDown_0.3s_ease-out_forwards]' : 'animate-[slideUp_0.2s_ease-in_forwards]'
          }`}
        >
          <div className="w-full flex flex-col ml-4 bg-lightbg-headerPrimary rounded-md overflow-hidden space-y-1 p-1 relative z-[999999]">
            {languages
              ?.filter((lang) => lang.Value !== language)
              .map((lang, index) => (
                <button
                  key={lang.Name}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAllMenuOpen(false);
                    setIsCurrentMenuOpen(false);
                    setLanguage(lang.Value);
                    changeLanguage(lang.Value);
                  }}
                  className={`
                    w-full text-left pl-6 pr-6 py-3  text-base 
                    bg-lightbg-headerPrimary transition-colors duration-200 
                    hover:bg-lightbg-headerSecondary focus:bg-lightbg-headerSecondary 
                    focus:outline-none rounded-md relative z-[999999]
                    ${index === 0 ? 'animate-delay-75' : ''}
                    ${index === 1 ? 'animate-delay-150' : ''}
                  `}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  {lang.Name}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitchHamburger;
